import { functions } from "../firebase";
import { RoundtableAgendaItem } from "../../types/Roundtable";

type GenerateParams = {
  title: string;
  goal: string;
  communityId: string;
};

// Deterministic agenda used when the AI call fails or returns nothing, so a
// roundtable is never written without a usable agenda. The host can still tap
// "Refresh agenda" in the app to get the author-led AI version later.
export const buildFallbackAgenda = ({
  title,
  goal,
}: {
  title: string;
  goal: string;
}): RoundtableAgendaItem[] => {
  const focus = goal.trim() || title.trim() || "the shared goal";

  return [
    {
      id: "1-context-round",
      timeLabel: "0-10 min",
      title: "Context round",
      prompt: `Each person shares what makes ${focus.toLowerCase()} relevant to them right now.`,
    },
    {
      id: "2-pattern-scan",
      timeLabel: "10-25 min",
      title: "Pattern scan",
      prompt: "Look for repeated challenges, goals, and useful experience across the room.",
    },
    {
      id: "3-peer-working-session",
      timeLabel: "25-45 min",
      title: "Peer working session",
      prompt:
        "Choose 2-3 active challenges and ask the room for examples, blind spots, useful questions, and relevant connections.",
    },
    {
      id: "4-commitments",
      timeLabel: "45-60 min",
      title: "Commitments",
      prompt:
        "Each person names one next step and one person they should follow up with after the roundtable.",
    },
  ];
};

const isAgendaItem = (item: any): item is RoundtableAgendaItem =>
  !!item &&
  typeof item.id === "string" &&
  typeof item.timeLabel === "string" &&
  typeof item.title === "string" &&
  typeof item.prompt === "string";

// Generates an author-led agenda via the callable. Always resolves: any failure
// falls back to the deterministic agenda so roundtable creation never blocks.
export const generateRoundtableAgenda = async ({
  title,
  goal,
  communityId,
}: GenerateParams): Promise<RoundtableAgendaItem[]> => {
  const fallback = buildFallbackAgenda({ title, goal });

  try {
    const callable = functions.httpsCallable("generateRoundtableAgenda");
    const result = await callable({ title, goal, communityId });
    const agenda = (result?.data as { agenda?: unknown })?.agenda;

    if (Array.isArray(agenda)) {
      const normalized = agenda.filter(isAgendaItem);
      if (normalized.length >= 3) return normalized;
    }
  } catch (error) {
    console.warn("[roundtables] agenda generation failed, using fallback", error);
  }

  return fallback;
};
